import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminAuthAPI from '../../api/admin/auth';
import PasswordInput from '../../components/PasswordInput';
import './AdminPasswordSetup.css';

const AdminPasswordSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Extract token from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('No password setup token found in URL');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await AdminAuthAPI.completePasswordSetup(token, password);
      
      if (response.success) {
        setSuccess(true);
        
        // Store the admin token and user data
        localStorage.setItem('admin_token', response.access_token);
        localStorage.setItem('admin_user', JSON.stringify(response.admin_user));
        localStorage.setItem('admin_token_expires', Date.now() + (response.expires_in * 1000));
        
        // Redirect to admin dashboard after a short delay
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 2000);
      } else {
        setError(response.error || 'Password setup failed');
      }
    } catch (error) {
      console.error('Password setup error:', error);
      setError('Password setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="admin-password-setup">
        <div className="setup-container">
          <div className="setup-card">
            <div className="setup-header">
              <h1>Password Setup Complete!</h1>
              <div className="success-icon">✅</div>
            </div>
            <div className="setup-content">
              <p>Your admin password has been set successfully.</p>
              <p>Redirecting you to the admin dashboard...</p>
              <div className="loading-spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-password-setup">
      <div className="setup-container">
        <div className="setup-card">
          <div className="setup-header">
            <h1>Set Up Your Admin Password</h1>
            <p>Complete your admin account setup by creating a secure password</p>
          </div>
          
          <form onSubmit={handleSubmit} className="setup-form">
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter a strong password"
                className="form-input"
                minLength="8"
                autoComplete="new-password"
                showStrengthIndicator={true}
                userInputs={[]} // Could add admin email if available
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
                className="form-input"
                minLength="8"
                autoComplete="new-password"
                confirmValue={password}
                showConfirmationStatus={true}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !token}
              className="setup-button"
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Setting Up Password...
                </>
              ) : (
                'Set Password'
              )}
            </button>
          </form>

          <div className="setup-footer">
            <p>
              Need help? <a href="/admin/login">Back to Login</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPasswordSetup; 